import "./ranking-stats.scss";
import { FaPerson } from "react-icons/fa6";
import { GrDocumentPerformance } from "react-icons/gr";
import { ReactNode } from "react";
import classNames from "classnames";
import { numberFormat } from "@shared/utility/formats/number-format";
import { EmployeePerformanceModel } from "@core/model/query-model-report";
import { DashboardRanking } from "@core/model/dashboard-ranking.enum";

/* eslint-disable-next-line */
export interface RankingStatsProps {
  data: Record<string, EmployeePerformanceModel[]>;
  rankingNavigate: (title: DashboardRanking) => void;
}

export function RankingStats({ data, rankingNavigate }: RankingStatsProps) {
  const timeElement = (model: EmployeePerformanceModel[]) => {
    const employees = model.map((item) => {
      const itemTally = item.tally ?? 0;
      return (
        <div key={item.user_code} className="flex justify-between">
          <div className="flex gap-2 items-center">
            <FaPerson />
            <span>
              {item.person_first_name} {item.person_last_name}
            </span>
          </div>
          <span>
            {itemTally < 60
              ? `${itemTally} ms`
              : `${numberFormat(itemTally / 60, 1)} hr`}
          </span>
        </div>
      );
    });

    return (
      <div>
        {model.length > 0 ? (
          <>
            <div className="flex justify-between mb-2">
              <span className="text-gray-500">Employee</span>
              <span className="text-gray-500">Time</span>
            </div>
            {employees}
          </>
        ) : (
          <p className="text-center">No Data</p>
        )}
      </div>
    );
  };

  const dayElement = (model: EmployeePerformanceModel[]) => {
    const employees = model.map((item) => (
      <div key={item.user_code} className="flex justify-between">
        <div className="flex gap-2 items-center">
          <FaPerson />
          <span>
            {item.person_first_name} {item.person_last_name}
          </span>
        </div>
        <span>{item.tally}</span>
      </div>
    ));

    return (
      <div>
        {model.length > 0 ? (
          <>
            <div className="flex justify-between mb-2">
              <span className="text-gray-500">Employee</span>
              <span className="text-gray-500">Days</span>
            </div>
            {employees}
          </>
        ) : (
          <p className="text-center">No Data</p>
        )}
      </div>
    );
  };

  const statCard = (
    title: DashboardRanking,
    colorClass: string,
    content: ReactNode
  ) => {
    return (
      <div
        className="flex flex-col p-4 border rounded-lg gap-2 cursor-pointer"
        onClick={() => rankingNavigate(title)}
      >
        <div className="flex items-center gap-2">
          <div className={classNames("p-3 rounded-lg", colorClass)}>
            <GrDocumentPerformance />
          </div>
          <h3>{title}</h3>
        </div>
        <div className="m-0">{content}</div>
      </div>
    );
  };

  return (
    <div id="RankingStats" className="ranking-stats mt-6">
      <h3>Category Ranking</h3>
      <section className="grid grid-cols-3 gap-6 mt-8">
        {statCard(
          DashboardRanking.Absent,
          "bg-green-200",
          <div className="m-0">{dayElement(data?.absent ?? [])}</div>
        )}

        {statCard(
          DashboardRanking.Amended,
          "bg-blue-200",
          <div className="m-0">{dayElement(data?.amended ?? [])}</div>
        )}

        {statCard(
          DashboardRanking.Late,
          "bg-amber-200",
          <div className="m-0">{dayElement(data?.late ?? [])}</div>
        )}

        {statCard(
          DashboardRanking.OnLeave,
          "bg-rose-200",
          <div className="m-0">{timeElement(data?.onleave ?? [])}</div>
        )}

        {statCard(
          DashboardRanking.Overtime,
          "bg-sky-200",
          <div className="m-0">{timeElement(data?.overtime ?? [])}</div>
        )}

        {statCard(
          DashboardRanking.Undertime,
          "bg-violet-200",
          <div className="m-0">{timeElement(data?.undertime ?? [])}</div>
        )}
      </section>
    </div>
  );
}

export default RankingStats;
